import { useCallback, useEffect, useRef, useState } from "react";

import * as api from "@/api/client";
import type { LiveMetrics, PairWatchSnapshot } from "@/types/api";

/**
 * Polls the Vigil pair-watch snapshot (every 60s) and live metrics (every
 * 20s). Plain useEffect + setInterval — no external state library needed at
 * this scale. Ported from
 * products/x2mail-admin/src/hooks/useSnapshotPoll.ts.
 */
const SNAPSHOT_INTERVAL_MS = 60_000;
const LIVE_INTERVAL_MS = 20_000;

export interface SnapshotPollState {
  snapshots: PairWatchSnapshot[];
  live: LiveMetrics | null;
  loading: boolean;
  error: string | null;
}

export function useVigilSnapshotPoll(): SnapshotPollState {
  const [snapshots, setSnapshots] = useState<PairWatchSnapshot[]>([]);
  const [live, setLive] = useState<LiveMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSnapshot = useCallback(async () => {
    try {
      const res = await api.getVigilSnapshot();
      setSnapshots(res.snapshots);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Błąd pobierania snapshotu");
    }
  }, []);

  const loadLive = useCallback(async () => {
    try {
      const res = await api.getVigilLive();
      setLive(res.live);
    } catch {
      // Live metrics failure is non-fatal — snapshot remains primary.
    }
  }, []);

  // Track mounted state to avoid setState on an unmounted component.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function initialLoad() {
      setLoading(true);
      await Promise.all([loadSnapshot(), loadLive()]);
      if (!cancelled) setLoading(false);
    }

    void initialLoad();

    const snapshotTimer = setInterval(() => {
      void loadSnapshot();
    }, SNAPSHOT_INTERVAL_MS);

    const liveTimer = setInterval(() => {
      void loadLive();
    }, LIVE_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(snapshotTimer);
      clearInterval(liveTimer);
    };
  }, [loadSnapshot, loadLive]);

  return { snapshots, live, loading, error };
}
